import * as React from 'react';
import { Column, Table } from '@tanstack/react-table';
import { IconFilter, IconCheck } from '@tabler/icons-react';
import { Text, Menu, ActionIcon, createStyles } from '@mantine/core';

// Hooks
import { useEnumDataSet } from '../../Form/hooks/useEnumDataSet';

const useStyles = createStyles((theme) => ({
    menuItem: {
        '.mantine-Menu-itemLabel': {
            display: 'flex',
            flex: 1,
            gap: '10px',
            justifyContent: 'space-between',
            alignItems: 'center'
        }
    },
    menuItemSelected: {
        color: theme.colors.blue,
        backgroundColor: theme.colors.blue[0]
    },
    menuItemText: {
        textTransform: 'capitalize'
    },
    hidden: {
        opacity: 0
    }
}));

interface ColumnFilterCommonProps {
    column: Column<any, unknown>;
    table: Table<any>;
    className: string;
}

interface ColumnFilterEnumDataSetProps extends ColumnFilterCommonProps {
    dataSetKey: string;
}

export const ColumnFilterEnumDataSet: React.FC<ColumnFilterEnumDataSetProps> = ({
    column,
    table,
    className = '',
    dataSetKey
}) => {
    const { getTranslatedDataSet } = useEnumDataSet();
    const [updatedState, setUpdatedState] = React.useState<string[]>([]);
    const { classes, cx } = useStyles();
    const columnFilterValue = (column.getFilterValue() ?? []) as string[];

    return (
        <Menu
            shadow="md"
            closeOnItemClick={false}
            withinPortal={true}
            onOpen={() => setUpdatedState(columnFilterValue)}
            onClose={() => {
                column.setFilterValue(updatedState);
                setUpdatedState([]);
            }}
        >
            <Menu.Target>
                <ActionIcon
                    variant={columnFilterValue.length ? 'light' : 'subtle'}
                    color={columnFilterValue.length ? 'blue' : undefined}
                    className={className}
                >
                    <IconFilter size={14} />
                </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
                {getTranslatedDataSet(dataSetKey).map((item: any, index) => (
                    <Menu.Item
                        key={index}
                        onClick={() => {
                            const currentState = [...updatedState];

                            if (!currentState.length) {
                                setUpdatedState([item.value]);
                            } else if (currentState.includes(item.value)) {
                                const index = currentState.indexOf(item.value);
                                currentState.splice(index, 1);
                                setUpdatedState(currentState);
                            } else {
                                setUpdatedState([...currentState, item.value]);
                            }
                        }}
                        className={cx(classes.menuItem, {
                            [classes.menuItemSelected]: updatedState.includes(item.value)
                        })}
                    >
                        <Text className={classes.menuItemText}>{item.label}</Text>
                        <IconCheck
                            size={14}
                            className={cx('', {
                                [classes.hidden]: !updatedState.includes(item.value)
                            })}
                        />
                    </Menu.Item>
                ))}
            </Menu.Dropdown>
        </Menu>
    );
};

export const ColumnFilterCellValues: React.FC<ColumnFilterCommonProps> = ({ column, table, className = '' }) => {
    const { classes, cx } = useStyles();
    const firstValue = table.getPreFilteredRowModel().flatRows[0]?.getValue(column.id);
    const columnFilterValue = column.getFilterValue();
    const sortedUniqueValues = React.useMemo(
        () =>
            typeof firstValue === 'boolean'
                ? [
                      { label: 'Yes', value: true },
                      { label: 'No', value: false }
                  ]
                : Array.from(column.getFacetedUniqueValues().keys()).sort(),
        [column.getFacetedUniqueValues()]
    );

    return (
        <Menu shadow="md">
            <Menu.Target>
                <ActionIcon
                    variant={typeof columnFilterValue !== 'undefined' ? 'light' : 'subtle'}
                    color={typeof columnFilterValue !== 'undefined' ? 'blue' : undefined}
                    className={className}
                >
                    <IconFilter size={14} />
                </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
                {sortedUniqueValues.slice(0, 5000).map((value: any, index) => (
                    <Menu.Item
                        key={index}
                        onClick={() => {
                            if (column.getFilterValue() === (value?.value ?? value)) {
                                column.setFilterValue(undefined);
                            } else {
                                column.setFilterValue(value?.value ?? value);
                            }
                        }}
                        className={cx(classes.menuItem, {
                            [classes.menuItemSelected]: column.getFilterValue() === (value?.value ?? value)
                        })}
                    >
                        <Text className={classes.menuItemText}>{(value?.label ?? value ?? '').toString()}</Text>
                        <IconCheck
                            size={14}
                            className={cx('', {
                                [classes.hidden]: column.getFilterValue() !== (value?.value ?? value)
                            })}
                        />
                    </Menu.Item>
                ))}
            </Menu.Dropdown>
        </Menu>
    );
};
